import axios from 'axios';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
const DEFAULT_PRODUCTION_API = 'https://api.pimlify.com';

const normalizeBaseUrl = (value: string) => value.replace(/\/$/, '');

/** Strip /api or /api/v1 suffix so public routes always hit /api/public/dpp/... */
function resolveApiRoot(): string {
  const envBase = import.meta.env.VITE_API_URL;
  if (envBase && typeof envBase === 'string') {
    return normalizeBaseUrl(envBase).replace(/\/api\/v1$/, '').replace(/\/api$/, '');
  }

  if (typeof window !== 'undefined') {
    const injectedBase = (window as { __API_BASE__?: string }).__API_BASE__;
    if (injectedBase && typeof injectedBase === 'string') {
      return normalizeBaseUrl(injectedBase).replace(/\/api\/v1$/, '').replace(/\/api$/, '');
    }

    const { hostname } = window.location;
    if (LOCAL_HOSTS.has(hostname)) {
      if (import.meta.env.DEV) {
        return '';
      }
      return 'http://localhost:3000';
    }

    return DEFAULT_PRODUCTION_API;
  }

  return 'http://localhost:3000';
}

const API_ROOT = resolveApiRoot();

/** Absolute URL for a public DPP API path, e.g. getPublicApiUrl('/v1/passports/x/jsonld'). */
export function getPublicApiUrl(suffix: string): string {
  return `${API_ROOT}/api/public/dpp${suffix}`;
}

const api = axios.create({
  baseURL: API_ROOT,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function publicDppPath(suffix: string): string {
  return `/api/public/dpp${suffix}`;
}

export interface DPPMaterial {
  id: string;
  material_name: string;
  material_type: string;
  percentage?: number;
  weight_grams?: number;
  origin_country?: string;
}

export interface DPPEnvironmental {
  carbon_footprint_kg_co2?: number;
  water_usage_liters?: number;
  recyclability_percentage?: number;
  energy_consumption_kwh?: number;
}

export interface DPPCertificate {
  id: string;
  certificate_type: string;
  certificate_number?: string;
  issuing_authority?: string;
  issue_date?: string;
  expiry_date?: string;
}

export interface DPPEndOfLife {
  recycling_instructions?: string;
  disposal_method?: string;
  recyclability_status?: string;
  repair_instructions?: string;
  reuse_instructions?: string;
  spare_parts_availability?: boolean | string;
}

export interface DPPData {
  dpp: {
    id: string;
    unique_identifier: string;
    status: string;
    version: number;
    published_at?: string;
  };
  branding?: {
    organisation_name?: string | null;
    logo_url?: string | null;
    primary_color?: string | null;
  } | null;
  product?: {
    id: string;
    name?: string;
    sku?: string;
    description?: string;
    short_description?: string;
    weight?: number | null;
    dimensions?: Record<string, unknown> | null;
    price?: number | null;
    metadata?: Record<string, unknown>;
    category_name?: string | null;
  } | null;
  materials?: DPPMaterial[];
  environmental?: DPPEnvironmental;
  certificates?: DPPCertificate[];
  endOfLife?: DPPEndOfLife;
  template_categories?: Array<{
    id: string;
    category_key: string;
    category_name: string;
    sort_order: number;
    attributes?: Array<{
      id: string;
      attribute_key: string;
      attribute_name: string;
      attribute_description?: string | null;
      help_text?: string | null;
      data_type?: string;
      sort_order?: number;
      display_group?: string | null;
      input_type?: string | null;
      show_in_detail?: boolean;
      channel_requirements?: Partial<Record<'website' | 'datasheet' | 'dpp', boolean>>;
    }>;
  }>;
  documents?: Array<{
    name: string;
    type?: string;
    size?: string;
    url?: string;
    document_type?: string | null;
  }>;
  documents_by_type?: Record<string, Array<{
    name: string;
    type?: string;
    size?: string;
    url?: string;
    document_type?: string | null;
  }>>;
}

function publicResolvePath(gs1Path: string): string {
  const normalized = gs1Path.startsWith('/') ? gs1Path.slice(1) : gs1Path;
  return `/api/public/resolve/${normalized}?format=json`;
}

export const apiService = {
  publicResolver: {
    resolve: async (gs1Path: string) => {
      return api.get(publicResolvePath(gs1Path));
    },
  },
  publicDPP: {
    getByUniqueId: async (uniqueId: string, locale?: string): Promise<{ data: { success: boolean; data: DPPData | Record<string, unknown> } }> => {
      const localeQuery = locale ? `?locale=${encodeURIComponent(locale)}` : '';
      try {
        return await api.get(publicDppPath(`/passports/${encodeURIComponent(uniqueId)}${localeQuery}`));
      } catch (primaryError: unknown) {
        try {
          return await api.get(publicDppPath(`/${encodeURIComponent(uniqueId)}${localeQuery}`));
        } catch (legacyError: unknown) {
          const axiosErr = legacyError as { response?: { data?: { message?: string }; status?: number } };
          const primaryAxios = primaryError as { response?: { data?: { message?: string }; status?: number } };
          const legacyMessage = axiosErr.response?.data?.message;
          const primaryMessage = primaryAxios.response?.data?.message;
          if (legacyMessage) throw legacyError;
          if (primaryMessage) throw primaryError;
          throw legacyError;
        }
      }
    },
    getByQRCode: async (qrCodeId: string): Promise<{ data: { success: boolean; data: DPPData | Record<string, unknown> } }> => {
      try {
        const resolved = await api.get(publicDppPath(`/data-carriers/${encodeURIComponent(qrCodeId)}/resolve`));
        const snap = resolved.data?.data?.snapshot;
        const redirect = resolved.data?.data?.redirect_url;
        if (snap?.uniqueIdentifier) {
          return api.get(publicDppPath(`/passports/${encodeURIComponent(snap.uniqueIdentifier)}`));
        }
        if (redirect && typeof redirect === 'string') {
          const match = redirect.match(/\/dpp\/([^/?#]+)/);
          if (match?.[1]) {
            return api.get(publicDppPath(`/passports/${encodeURIComponent(match[1])}`));
          }
        }
      } catch {
        // fall through to legacy QR route
      }
      return api.get(publicDppPath(`/qr/${encodeURIComponent(qrCodeId)}`));
    },
    getTraceability: async (uniqueId: string): Promise<unknown> => {
      return api.get(publicDppPath(`/${uniqueId}/traceability`));
    },
  },
};
